/**
 * The heart of capture-first: what you said, what Hondo will file, one obvious action. Every fact
 * is visible and tappable to fix on the spot (date, animal, amount) without opening the whole form.
 * Values Hondo guessed or filled in carry a small marker that explains itself. Questions appear only
 * when the answer changes the record. Filing, leaving out and "Not this" can all be undone.
 */
import { useEffect, useImperativeHandle, useMemo, useState, type Ref as ReactRef } from "react";
import { Link } from "react-router";
import { ENTITY_FIELDS, type Basis, type CaptureDTO, type EventInput, type Field, type ObservedTime, type ProposalDTO, type ProposalItem, type SubjectRef } from "../../contract";
import { api } from "../lib/api";
import { formatDate, relativeDay, timeAgo, useFormat } from "../lib/format";
import { t } from "../lib/i18n";
import { invalidateAll, useApi } from "../lib/query";
import { useCan, useMe, useRanchToday } from "../lib/session";
import { registry } from "../registry";
import { EventFormFor, WhenInput } from "../forms/EventForm";
import { FieldInput, SchemaForm } from "../forms/SchemaForm";
import { RefPicker, useLabels } from "../forms/pickers";
import { Button, Chip, Icon, IconButton, RichText, Sheet, Spinner, TextArea, TextInput, cx, toast, undoable } from "../ui/kit";
import { formatValue, itemTitle } from "./describe";

const WORKING = new Set(["uploading", "queued", "processing"]);

/** Follow a capture until the agent is done with it. */
export function useCapture(id: string | null) {
  return useApi<CaptureDTO>(id ? `/captures/${id}` : null, {
    refetchInterval: (q) => (q.state.data && !WORKING.has(q.state.data.status) ? false : 1200),
  });
}

export function CaptureStatus({ capture }: { capture: CaptureDTO }) {
  const map: Record<string, { label: string; tone: "good" | "warn" | "bad" | "info" | "neutral"; icon: string }> = {
    uploading: { label: "Sending", tone: "info", icon: "upload" },
    queued: { label: "Working on it", tone: "info", icon: "spinner" },
    processing: { label: "Working on it", tone: "info", icon: "spinner" },
    needs_review: { label: "Needs a look", tone: "warn", icon: "warn" },
    filed: { label: "Filed", tone: "good", icon: "done" },
    answered: { label: "Answered", tone: "good", icon: "done" },
    failed: { label: "Needs attention", tone: "bad", icon: "warn" },
    discarded: { label: "Not filed", tone: "neutral", icon: "archive" },
  };
  const s = map[capture.status] ?? map.queued;
  return <Chip tone={s.tone} icon={s.icon}>{t(s.label)}</Chip>;
}

// ---------------------------------------------------------------- where a value came from

/** Marks an English catalog key (collected by the i18n check); translated with t() where shown. */
const tr = (english: string) => english;

const BASIS_MARK: Partial<Record<Basis, { label: string; why: string }>> = {
  inferred: { label: tr("guessed"), why: tr("Hondo guessed this from what was said. Tap the fact to change it.") },
  default: { label: tr("default"), why: tr("Hondo used the usual value because none was said. Check it.") },
  calculated: { label: tr("worked out"), why: tr("Worked out from other records.") },
  linked: { label: tr("matched"), why: tr("Matched to a record Hondo found.") },
  imported: { label: tr("imported"), why: tr("From an imported file.") },
  edited: { label: tr("changed"), why: tr("You changed this.") },
};

/** True when a value wasn't said outright, so it deserves a second look. */
export const isFilledIn = (basis: Basis | undefined) => !!basis && basis !== "stated" && basis !== "edited";

export function EvidenceMark({ basis }: { basis: Basis | undefined }) {
  const [open, setOpen] = useState(false);
  const mark = basis ? BASIS_MARK[basis] : undefined;
  if (!mark) return null;
  return (
    <span className={cx("evidence-mark", `basis-${basis}`)}>
      <button type="button" className="evidence-btn" aria-expanded={open} title={t(mark.why)} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>{t(mark.label)}</button>
      {open && <span className="evidence-why" role="note">{t(mark.why)}</span>}
    </span>
  );
}

// ---------------------------------------------------------------- facts you can fix in place

type FactEdit = { kind: "when" } | { kind: "subject"; role: string; target: SubjectRef["kind"]; many: boolean } | { kind: "field"; name: string; field: Field } | { kind: "due" };
interface FactChip { key: string; label: string; value: string; basis?: Basis; edit?: FactEdit }

const INLINE_KINDS = new Set(["quantity", "number", "date", "enum", "ref", "money", "boolean", "tag", "text"]);
const inlineField = (f: Field) => INLINE_KINDS.has(f.kind) && !(f.kind === "text" && f.multiline) && !(f.kind === "enum" && f.multiple) && !(f.kind === "ref" && f.multiple);

function factsFor(item: ProposalItem, fmt: ReturnType<typeof useFormat>, today: string): FactChip[] {
  const ev = item.evidence ?? {};
  if (item.op === "event") {
    const def = registry.event(item.type);
    const out: FactChip[] = [];
    const when = item.occurred.value ? (item.occurred.precision === "date" || item.occurred.precision === "minute" ? relativeDay(item.occurred.value, today) : formatDate(item.occurred.value, item.occurred.precision)) : t("Date not known");
    out.push({ key: "when", label: t("When"), value: when, basis: ev.occurred, edit: { kind: "when" } });
    for (const spec of def?.subjects ?? []) {
      const refs = item.subjects.filter((s) => s.role === spec.role);
      if (!refs.length && !spec.required) continue;
      const names = refs.map((s) => s.label ?? fmt.label(s.id));
      out.push({ key: `s:${spec.role}`, label: t(spec.label), value: names.length > 4 ? t("{{count}} animals", { count: names.length }) : names.length ? fmt.list(names) : t("Not set"), basis: ev[spec.role] ?? ev.subjects,
        edit: { kind: "subject", role: spec.role, target: spec.kind, many: !!spec.many } });
    }
    for (const [name, field] of Object.entries(def?.fields ?? {})) {
      const value = item.data[name];
      if (value === undefined || value === null || value === "" || !inlineField(field)) continue;
      out.push({ key: `f:${name}`, label: t(field.label), value: formatValue(field, value, fmt), basis: ev[name], edit: { kind: "field", name, field } });
    }
    return out.slice(0, 8);
  }
  if (item.op === "create") {
    const fields = ENTITY_FIELDS[item.entity];
    return Object.entries(fields).filter(([k, f]) => item.data[k] !== undefined && item.data[k] !== null && item.data[k] !== "" && inlineField(f)).slice(0, 7)
      .map(([name, field]) => ({ key: `f:${name}`, label: t(field.label), value: formatValue(field, item.data[name], fmt), basis: ev[name], edit: { kind: "field" as const, name, field } }));
  }
  if (item.op === "task") {
    return [
      { key: "due", label: t("Due"), value: item.data.due_at ? relativeDay(item.data.due_at, today) : t("No date"), basis: ev.due_at, edit: { kind: "due" } },
      ...(item.data.assignee_id ? [{ key: "who", label: t("For"), value: fmt.label(item.data.assignee_id) }] : []),
    ];
  }
  return [];
}

function applyFact(item: ProposalItem, edit: FactEdit, value: unknown): ProposalItem {
  const evidence = { ...(item.evidence ?? {}) };
  if (edit.kind === "when" && item.op === "event") return { ...item, occurred: value as ObservedTime, evidence: { ...evidence, occurred: "edited" } };
  if (edit.kind === "subject" && item.op === "event") {
    const ids = (Array.isArray(value) ? value : value ? [value] : []) as string[];
    const others = item.subjects.filter((s) => s.role !== edit.role);
    return { ...item, subjects: [...others, ...ids.map((id) => ({ kind: edit.target, id, role: edit.role }))], evidence: { ...evidence, [edit.role]: "edited" } };
  }
  if (edit.kind === "field" && (item.op === "event" || item.op === "create")) {
    return { ...item, data: { ...item.data, [edit.name]: value }, evidence: { ...evidence, [edit.name]: "edited" } } as ProposalItem;
  }
  if (edit.kind === "due" && item.op === "task") return { ...item, data: { ...item.data, due_at: (value as string) || null }, evidence: { ...evidence, due_at: "edited" } };
  return item;
}

function FactEditor({ item, edit, onSave, onCancel }: { item: ProposalItem; edit: FactEdit; onSave: (v: unknown) => void; onCancel: () => void }) {
  const initial = edit.kind === "when" && item.op === "event" ? item.occurred
    : edit.kind === "subject" && item.op === "event" ? (edit.many ? item.subjects.filter((s) => s.role === edit.role).map((s) => s.id) : item.subjects.find((s) => s.role === edit.role)?.id ?? null)
    : edit.kind === "field" && (item.op === "event" || item.op === "create") ? item.data[edit.name]
    : edit.kind === "due" && item.op === "task" ? item.data.due_at ?? "" : undefined;
  const [value, setValue] = useState<unknown>(initial);
  const labels = item.op === "event" ? Object.fromEntries(item.subjects.filter((s) => s.label).map((s) => [s.id, s.label!])) : {};
  return (
    <form className="fact-editor" onSubmit={(e) => { e.preventDefault(); onSave(value); }} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); } }}>
      {edit.kind === "when" && <WhenInput value={value as ObservedTime} onChange={setValue} />}
      {edit.kind === "subject" && <RefPicker target={edit.target} multiple={edit.many} value={value as string | string[] | null} onChange={setValue} labels={labels} autoFocus />}
      {edit.kind === "field" && <FieldInput name={edit.name} field={{ ...edit.field, help: undefined }} value={value} onChange={setValue} errors={{}} />}
      {edit.kind === "due" && <TextInput type="date" aria-label={t("Due")} value={(value as string) ?? ""} onChange={(e) => setValue(e.target.value)} autoFocus />}
      <div className="fact-editor-actions">
        <Button type="submit" size="sm" variant="primary" icon="check">{t("Use this")}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>{t("Cancel")}</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- the card

/** What the Review page can ask a card to do from the keyboard. */
export interface ProposalActions {
  /** File what's chosen. Resolves false when there was nothing to file. */
  file(): Promise<boolean>;
  /** Open the first detail that can be edited. */
  edit(): void;
  /** "Not this", with Undo. */
  reject(): void;
  canFile: boolean;
}

export function ProposalCard({ capture, compact, onDone, active, actions }: {
  capture: CaptureDTO; compact?: boolean; onDone?: () => void;
  /** Highlighted as the current card in the review queue. */
  active?: boolean;
  /** Receives file/edit/reject so keyboard shortcuts can drive the card. */
  actions?: ReactRef<ProposalActions>;
}) {
  const p = capture.proposal;
  const can = useCan();
  const { data: me } = useMe();
  const today = useRanchToday();
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, ProposalItem>>({});
  const [editing, setEditing] = useState<ProposalItem | null>(null);
  const [inline, setInline] = useState<{ itemId: string; key: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [setAside, setSetAside] = useState(false);
  const [fixing, setFixing] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  useEffect(() => { setExcluded(new Set()); setEdits({}); setInline(null); }, [p?.id]);
  const words = capture.text ?? capture.transcript;
  const mine = me?.membership?.id === capture.created_by?.id;
  const canDecide = can("review") || (mine && can("record"));

  const items = (p?.items ?? []).map((i) => edits[i.id] ?? i);
  // Labels for animals or places picked while fixing a fact.
  const picked = useLabels(items.flatMap((i) => (i.op === "event" ? i.subjects.filter((s) => !p?.labels[s.id] && !s.label).map((s) => ({ kind: s.kind, id: s.id })) : [])));
  const labels = useMemo(() => ({ ...(p?.labels ?? {}), ...picked }), [p?.labels, picked]);
  const fmt = useFormat(labels);
  const pending = items.filter((i) => i.status === "pending" || i.status === "failed");
  const chosen = pending.filter((i) => !excluded.has(i.id));
  const filedItems = items.filter((i) => i.status === "filed");
  const openChoices = p?.questions.some((q) => !q.answer && q.options?.length) ?? false;
  const canFile = !!(canDecide && p && chosen.length && !openChoices && !setAside);

  const undo = async (id: string) => {
    await api.post(`/proposals/${id}/undo`);
    await invalidateAll();
    toast(t("Undone. It's waiting for a look."));
  };
  const file = async (): Promise<boolean> => {
    if (!p || !canFile) return false;
    setBusy("file");
    try {
      const decisions = pending.map((i) => ({ id: i.id, action: excluded.has(i.id) ? "reject" : "accept", ...(edits[i.id] ? { item: edits[i.id] } : {}) }));
      const result = await api.post<ProposalDTO>(`/proposals/${p.id}/file`, { decisions });
      await invalidateAll();
      const failed = result.items.filter((i) => i.status === "failed");
      if (failed.length) toast(t("Some details need fixing: {{error}}", { error: failed[0].error ?? "" }), { tone: "bad" });
      else toast(t("Filed"), { tone: "good", action: { label: t("Undo"), run: () => void undo(p.id) } });
      if (!failed.length) onDone?.();
      return !failed.length;
    } catch (err) {
      toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
      return false;
    } finally { setBusy(null); }
  };
  const reject = () => {
    if (!p || !canDecide) return;
    undoable(t("Not filed. The note stays in history."), {
      apply: () => setSetAside(true),
      revert: () => setSetAside(false),
      commit: async () => { await api.post(`/proposals/${p.id}/reject`); await invalidateAll(); onDone?.(); },
    });
  };
  const firstEditable = pending.find((i) => i.op === "event" || i.op === "create");
  useImperativeHandle(actions, () => ({ file, edit: () => { if (firstEditable && canDecide) setEditing(firstEditable); }, reject, canFile }));

  const answer = async (questionId: string, value: string) => {
    setBusy(questionId);
    try {
      await api.post(`/captures/${capture.id}/answer`, { answers: [{ question_id: questionId, answer: value }] });
      await invalidateAll();
    } finally { setBusy(null); }
  };
  const retry = async () => { await api.post(`/captures/${capture.id}/retry`); await invalidateAll(); };
  const discard = () => undoable(t("Note discarded."), { apply: () => setSetAside(true), revert: () => setSetAside(false), commit: async () => { await api.post(`/captures/${capture.id}/discard`); await invalidateAll(); onDone?.(); } });
  const rerun = async (text: string) => {
    setBusy("fix");
    try { await api.patch(`/captures/${capture.id}`, { text }); setFixing(null); await invalidateAll(); toast(t("Hondo is reading it again.")); }
    catch (err) { toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); }
    finally { setBusy(null); }
  };
  const setItem = (id: string, next: ProposalItem) => setEdits((e) => ({ ...e, [id]: next }));
  const resetItem = (id: string) => setEdits((e) => { const n = { ...e }; delete n[id]; return n; });

  if (setAside) {
    return (
      <article className="proposal proposal-aside" id={`proposal-${capture.id}`} tabIndex={-1}>
        <span className="muted">{words ? `“${words.slice(0, 80)}”` : t("Note")}</span>
        <Button size="sm" variant="ghost" icon="undo" onClick={() => setSetAside(false)}>{t("Undo")}</Button>
      </article>
    );
  }

  const images = capture.files.filter((f) => f.mime.startsWith("image/"));
  const others = capture.files.filter((f) => !f.mime.startsWith("image/") && !f.mime.startsWith("audio/"));
  const canFix = canDecide && !!words && (capture.status === "needs_review" || capture.status === "failed");

  return (
    <article className={cx("proposal", compact && "proposal-compact", `proposal-${capture.status}`, active && "is-active")} id={`proposal-${capture.id}`} tabIndex={-1}
      aria-current={active || undefined} aria-label={words ? t("Note: {{text}}", { text: words.slice(0, 120) }) : t("Note")}>
      <header className="proposal-head">
        <span className="proposal-who">{capture.created_by?.label}{capture.created_by && " · "}{timeAgo(capture.created_at)}</span>
        <CaptureStatus capture={capture} />
      </header>

      {fixing !== null ? (
        <form className="fix-words" onSubmit={(e) => { e.preventDefault(); if (fixing.trim()) void rerun(fixing.trim()); }}>
          <TextArea value={fixing} onChange={(e) => setFixing(e.target.value)} rows={3} aria-label={t("What was said")} autoFocus />
          <div className="row-actions">
            <Button type="submit" size="sm" variant="primary" icon="reset" busy={busy === "fix"}>{t("Read it again")}</Button>
            <Button size="sm" variant="ghost" onClick={() => setFixing(null)}>{t("Cancel")}</Button>
          </div>
        </form>
      ) : words && (
        <div className="proposal-source">
          <blockquote className="proposal-quote">{words}</blockquote>
          {canFix && <button type="button" className="link small" onClick={() => setFixing(words)}>{capture.transcript && !capture.text ? t("Fix a word Hondo misheard") : t("Change the words")}</button>}
        </div>
      )}
      {capture.files.filter((f) => f.mime.startsWith("audio/")).map((f) => <audio key={f.id} controls preload="none" src={f.url} className="proposal-audio" aria-label={t("Recording")} />)}
      {images.length > 0 && (
        <div className="proposal-images">{images.map((f, i) => (
          <button key={f.id} type="button" className="proposal-thumb" onClick={() => setPhoto(f.url)} aria-label={t("Open photo {{n}}", { n: i + 1 })}><img src={f.url} alt="" loading="lazy" /></button>
        ))}</div>
      )}
      {others.length > 0 && <div className="proposal-files">{others.map((f) => <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="chip tone-neutral"><Icon name="file" size={14} />{f.name ?? t("File")}</a>)}</div>}

      {WORKING.has(capture.status) && <div className="proposal-working"><Spinner label={capture.status === "uploading" ? t("Sending…") : capture.progress ? capture.progress : t("Hondo is working on it…")} /></div>}
      {capture.status === "failed" && (
        <div className="proposal-error">
          <p>{capture.error ? t(capture.error) : t("We couldn't finish this note.")}</p>
          <div className="proposal-actions"><Button size="sm" onClick={retry} icon="reset">{t("Try again")}</Button><Button size="sm" variant="ghost" onClick={discard}>{t("Discard")}</Button></div>
        </div>
      )}

      {p?.answer && <div className="proposal-answer"><RichText text={p.answer.text} />
        {p.answer.citations.length > 0 && (
          <div className="citations">{p.answer.citations.slice(0, 12).map((c) => <Link key={`${c.kind}:${c.id}`} className="citation" to={c.kind === "animal" ? `/cattle/${c.id}` : c.kind === "event" ? `/history/${c.id}` : c.kind === "place" ? `/ranch/places/${c.id}` : c.kind === "job" ? `/work/jobs/${c.id}` : c.kind === "group" ? `/cattle/groups/${c.id}` : c.kind === "report_run" ? `/ranch/reports/runs/${c.id}` : "#"}>{c.label}</Link>)}</div>
        )}
      </div>}

      {p && items.length > 0 && (
        <ul className="proposal-items">
          {items.map((item) => {
            const { icon, title, detail } = itemTitle(item, fmt);
            const out = excluded.has(item.id) || item.status === "rejected";
            const def = item.op === "event" ? registry.event(item.type) : undefined;
            const editable = canDecide && (item.status === "pending" || item.status === "failed");
            const facts = factsFor(item, fmt, today);
            const openFact = inline?.itemId === item.id ? facts.find((f) => f.key === inline.key) : undefined;
            const filledIn = facts.filter((f) => isFilledIn(f.basis)).length;
            return (
              <li key={item.id} className={cx("proposal-item", out && "item-out", item.status === "filed" && "item-filed", item.status === "failed" && "item-failed")}>
                <span className={cx("item-icon", def && `tone-${def.tone}`)}><Icon name={icon} size={18} /></span>
                <div className="item-main">
                  <div className="item-title">{title}{def?.review === "always" && item.status === "pending" && <span className="item-flag">{t("always checked")}</span>}</div>
                  <div className="item-detail">{detail}</div>
                  {facts.length > 0 && !out && (
                    <ul className="facts-row" aria-label={t("Details")}>
                      {facts.map((f) => (
                        <li key={f.key} className={cx("fact-chip", isFilledIn(f.basis) && "is-filled", openFact?.key === f.key && "is-open")}>
                          {editable && f.edit ? (
                            <button type="button" className="fact-btn" aria-expanded={openFact?.key === f.key} onClick={() => setInline(openFact?.key === f.key ? null : { itemId: item.id, key: f.key })}
                              aria-label={t("Change {{label}}: {{value}}", { label: f.label, value: f.value })}>
                              <span className="fact-label">{f.label}</span><span className="fact-value">{f.value}</span>
                            </button>
                          ) : <span className="fact-btn is-static"><span className="fact-label">{f.label}</span><span className="fact-value">{f.value}</span></span>}
                          <EvidenceMark basis={f.basis} />
                        </li>
                      ))}
                    </ul>
                  )}
                  {openFact?.edit && (
                    <FactEditor key={openFact.key} item={item} edit={openFact.edit} onCancel={() => setInline(null)}
                      onSave={(v) => { setItem(item.id, applyFact(item, openFact.edit!, v)); setInline(null); }} />
                  )}
                  {filledIn > 0 && !out && item.status === "pending" && !compact && <p className="item-hint"><Icon name="info" size={14} />{filledIn === 1 ? t("One detail was filled in. Check it before filing.") : t("{{count}} details were filled in. Check them before filing.", { count: filledIn })}</p>}
                  {item.note && <div className="item-note"><Icon name="info" size={14} />{item.note}</div>}
                  {item.error && <div className="item-error"><Icon name="warn" size={14} />{item.error}</div>}
                  {edits[item.id] && editable && <button type="button" className="link small" onClick={() => resetItem(item.id)}><Icon name="undo" size={14} />{t("Undo my changes")}</button>}
                  {item.status === "filed" && item.result?.kind === "event" && <Link className="item-link" to={`/history/${item.result.id}`}>{t("View record")}</Link>}
                  {item.status === "filed" && item.result?.kind === "animal" && <Link className="item-link" to={`/cattle/${item.result.id}`}>{t("Open animal")}</Link>}
                </div>
                {editable && (
                  <div className="item-tools">
                    {(item.op === "event" || item.op === "create") && <IconButton icon="edit" label={t("Edit everything")} onClick={() => setEditing(item)} />}
                    <IconButton icon={out ? "plus" : "minus"} label={out ? t("Put it back") : t("Leave this out")} onClick={() => setExcluded((s) => { const n = new Set(s); if (n.has(item.id)) n.delete(item.id); else n.add(item.id); return n; })} />
                  </div>
                )}
                {item.status === "filed" && <Icon name="done" size={18} className="item-done" />}
              </li>
            );
          })}
        </ul>
      )}

      {p?.questions.filter((q) => !q.answer).map((q) => (
        <div key={q.id} className="proposal-question">
          <p><Icon name="help" size={18} />{q.text}</p>
          {q.options?.length ? (
            <div className="choice-chips">{q.options.map((o) => <Button key={o.value} size="sm" busy={busy === q.id} onClick={() => answer(q.id, o.value)}>{o.label}</Button>)}</div>
          ) : (
            <form className="question-input" onSubmit={(e) => { e.preventDefault(); if (answers[q.id]) void answer(q.id, answers[q.id]); }}>
              <TextInput value={answers[q.id] ?? ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} placeholder={t("Your answer")} aria-label={q.text} />
              <Button type="submit" size="sm" busy={busy === q.id}>{t("Send")}</Button>
            </form>
          )}
        </div>
      ))}

      {canDecide && p && pending.length > 0 && !openChoices && (
        <div className="proposal-actions">
          <Button variant="primary" size="lg" icon="check" busy={busy === "file"} disabled={!chosen.length} onClick={() => void file()} aria-keyshortcuts={active ? "Enter" : undefined}>
            {chosen.length === pending.length ? t("Looks right. File it") : t("File {{count}} of {{total}}", { count: chosen.length, total: pending.length })}
          </Button>
          <Button variant="ghost" onClick={reject} aria-keyshortcuts={active ? "X" : undefined}>{t("Not this")}</Button>
        </div>
      )}
      {!canDecide && p && pending.length > 0 && <p className="proposal-wait">{t("Waiting for a manager to look.")}</p>}
      {p && filedItems.length > 0 && pending.length === 0 && capture.status === "filed" && canDecide && !compact && (
        <div className="proposal-actions"><Button size="sm" variant="ghost" icon="undo" onClick={() => undo(p.id)}>{p.autofiled ? t("Filed automatically. Undo") : t("Undo")}</Button></div>
      )}
      {p && filedItems.length > 0 && pending.length === 0 && capture.status === "filed" && canDecide && compact && p.autofiled && (
        <div className="proposal-actions"><Button size="sm" variant="ghost" icon="undo" onClick={() => undo(p.id)}>{t("Filed automatically. Undo")}</Button></div>
      )}

      <Sheet open={!!editing} onClose={() => setEditing(null)} title={editing ? itemTitle(editing, fmt).title : ""}>
        {editing?.op === "event" && (
          <EventFormFor
            initial={{ type: editing.type, subjects: editing.subjects, occurred: editing.occurred, data: editing.data, place_id: editing.place_id, job_id: editing.job_id }}
            onCancel={() => setEditing(null)}
            onSubmit={async (input: EventInput) => {
              setItem(editing.id, { ...editing, ...input, evidence: { ...(editing.evidence ?? {}), ...Object.fromEntries(Object.keys(input.data).map((k) => [k, "edited"])) } } as ProposalItem);
              setEditing(null);
            }}
          />
        )}
        {editing?.op === "create" && <EditCreate item={editing} onSave={(item) => { setItem(item.id, item); setEditing(null); }} />}
      </Sheet>
      <Sheet open={!!photo} onClose={() => setPhoto(null)} title={t("Photo")} size="lg">
        {photo && <img src={photo} alt={words ? t("Photo sent with: {{text}}", { text: words.slice(0, 80) }) : t("Photo")} className="photo-full" />}
      </Sheet>
    </article>
  );
}

function EditCreate({ item, onSave }: { item: Extract<ProposalItem, { op: "create" }>; onSave: (i: ProposalItem) => void }) {
  const [data, setData] = useState(item.data);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...item, data, evidence: { ...(item.evidence ?? {}), ...Object.fromEntries(Object.keys(data).filter((k) => data[k] !== item.data[k]).map((k) => [k, "edited" as const])) } }); }}>
      <SchemaForm fields={ENTITY_FIELDS[item.entity]} value={data} onChange={setData} />
      <div className="form-actions"><Button variant="primary" type="submit" icon="check">{t("Use these details")}</Button></div>
    </form>
  );
}

/**
 * A note is "clear" when Hondo has no open question, nothing failed, and none of it is the kind of
 * record that always waits for a person (new animals, deaths, sales, money, stock). Those can be
 * filed together from the review queue.
 */
export function isClearNote(capture: CaptureDTO): boolean {
  const p = capture.proposal;
  if (capture.status !== "needs_review" || !p || p.status !== "pending") return false;
  if (p.questions.some((q) => !q.answer)) return false;
  const pending = p.items.filter((i) => i.status === "pending" || i.status === "failed");
  if (!pending.length || pending.some((i) => i.status === "failed")) return false;
  return pending.every((i) => {
    if (i.op === "event") return registry.event(i.type)?.review !== "always";
    return i.op === "task" || i.op === "membership" || i.op === "vocabulary";
  });
}
